/**
 * WholesaleChannelDrawer · Feb 2026 · Iter 210
 * ─────────────────────────────────────────────────────────────────────
 * Right-side sheet that opens when a seller clicks a Wholesale (manual
 * /CSV) channel card. Exposes three ingest tiers as sibling tabs:
 *
 *   · Upload CSV       → Tier 1 · rigid schema, template download,
 *                        drag-drop file, dry-run preview, submit
 *   · Email forwarding → Tier 2 · unique inbox address, copy button,
 *                        Gmail setup instructions
 *   · Scheduled import → Tier 3 · source URL + frequency, "Run now"
 *                        for on-demand pulls, last-run status
 */
import { useEffect, useRef, useState } from "react";
import api from "../../../../lib/api";
import { toast } from "sonner";
import {
  Upload, Download, Mail, Copy, Clock, Play, Loader2,
  Check, X, AlertTriangle, Building2, FileText, ExternalLink, Info,
} from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "../../../../components/ui/sheet";
import { Button } from "../../../../components/ui/button";
import { Input } from "../../../../components/ui/input";
import { Label } from "../../../../components/ui/label";

const TABS = [
  { id: "csv",       label: "Upload CSV",       icon: Upload },
  { id: "email",     label: "Email forwarding", icon: Mail },
  { id: "scheduled", label: "Scheduled import", icon: Clock },
];

export default function WholesaleChannelDrawer({ channel, open, onClose, onImport }) {
  const [tab, setTab] = useState("csv");

  if (!channel) return null;

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto"
                    data-testid="wholesale-drawer">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Building2 className="w-4 h-4 text-slate-700"/>
            {channel.store_name}
          </SheetTitle>
          <SheetDescription>
            Wholesale channel · manual + automated ingest surfaces.
          </SheetDescription>
        </SheetHeader>

        {/* Tab strip */}
        <div className="mt-4 flex items-center gap-1 border-b border-border">
          {TABS.map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`wholesale-tab-${t.id}`}
              className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px inline-flex items-center gap-1.5 transition-colors ${
                tab === t.id
                  ? "border-foreground text-foreground"
                  : "border-transparent text-foreground/60 hover:text-foreground"
              }`}
            >
              <t.icon className="w-3.5 h-3.5"/>{t.label}
            </button>
          ))}
        </div>

        <div className="mt-5">
          {tab === "csv"       && <CsvUploadTab       channel={channel} onDone={onImport}/>}
          {tab === "email"     && <EmailForwardingTab channel={channel}/>}
          {tab === "scheduled" && <ScheduledImportTab channel={channel} onDone={onImport}/>}
        </div>
      </SheetContent>
    </Sheet>
  );
}

// ───────────────────── Tab 1 · CSV Upload ─────────────────────

function CsvUploadTab({ channel, onDone }) {
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [csvText, setCsvText] = useState("");
  const [template, setTemplate] = useState(null);
  const fileRef = useRef(null);

  useEffect(() => {
    api.get("/dropship/channels/csv-template")
      .then(r => setTemplate(r.data))
      .catch(() => {});
  }, []);

  const downloadTemplate = () => {
    if (!template) return;
    const blob = new Blob([template.content], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = template.filename || "channel-orders-template.csv";
    document.body.appendChild(a); a.click(); a.remove();
    toast.success("Template downloaded");
  };

  const onFile = async (file) => {
    if (!file) return;
    const text = await file.text();
    setCsvText(text);
    setPreview(null);
  };

  const runPreview = async () => {
    if (!csvText.trim()) { toast.error("Choose a file or paste CSV first"); return; }
    setBusy(true);
    try {
      const r = await api.post(`/dropship/channels/${channel.id}/upload-csv`,
                               { csv_text: csvText, dry_run: true });
      setPreview({ mode: "preview", ...r.data });
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : (d?.message || "Preview failed"));
      if (d?.missing_columns) {
        setPreview({ mode: "schema_error", missing: d.missing_columns,
                     expected: d.expected_columns });
      }
    } finally { setBusy(false); }
  };

  const submit = async () => {
    if (!csvText.trim()) return;
    setBusy(true);
    try {
      const r = await api.post(`/dropship/channels/${channel.id}/upload-csv`,
                               { csv_text: csvText });
      toast.success(`Imported ${r.data.count_created} orders · ${r.data.count_duplicates} duplicates skipped`);
      setPreview({ mode: "result", ...r.data });
      setCsvText("");
      onDone?.();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : (d?.message || "Import failed"));
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="wholesale-csv-tab">
      <div className="rounded-lg border border-border bg-secondary/20 p-3 text-[11.5px] flex items-start gap-2">
        <Info className="w-3.5 h-3.5 shrink-0 mt-0.5 text-foreground/50"/>
        <div>
          <div className="font-medium text-[12px] text-foreground/80 mb-0.5">Rigid template</div>
          <div className="text-foreground/60">
            Your CSV must match our column names exactly. Download the template
            and use it as the source of truth. Required columns:&nbsp;
            <span className="mono text-[10.5px]">
              {(template?.required || []).join(", ")}
            </span>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <Button variant="outline" onClick={downloadTemplate}
                data-testid="wholesale-csv-template-download"
                className="rounded-full h-8 px-3 text-[12px]">
          <Download className="w-3.5 h-3.5 mr-1"/>Download template
        </Button>
        <input
          ref={fileRef}
          type="file"
          accept=".csv,text/csv"
          className="hidden"
          data-testid="wholesale-csv-file"
          onChange={e => onFile(e.target.files?.[0])}
        />
        <Button onClick={() => fileRef.current?.click()}
                data-testid="wholesale-csv-choose"
                className="rounded-full h-8 px-3 text-[12px]">
          <Upload className="w-3.5 h-3.5 mr-1"/>
          {csvText ? "Choose different file" : "Choose CSV"}
        </Button>
      </div>

      {csvText && (
        <>
          <div className="rounded-lg border border-border bg-white overflow-hidden">
            <div className="text-[10.5px] mono uppercase tracking-[0.14em] text-foreground/50 px-3 py-1.5 border-b border-border bg-secondary/30">
              CSV preview · {csvText.split("\n").filter(l => l.trim()).length - 1} data rows
            </div>
            <pre className="mono text-[10.5px] max-h-40 overflow-auto p-3 whitespace-pre">
{csvText.slice(0, 2000)}{csvText.length > 2000 ? "\n…" : ""}
            </pre>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={runPreview} disabled={busy}
                    data-testid="wholesale-csv-preview">
              {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin"/> : <FileText className="w-3.5 h-3.5 mr-1"/>}
              Preview
            </Button>
            <Button onClick={submit} disabled={busy}
                    data-testid="wholesale-csv-submit">
              {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin"/> : <Check className="w-3.5 h-3.5 mr-1"/>}
              Import orders
            </Button>
          </div>
        </>
      )}

      {preview && preview.mode === "preview" && (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50/40 p-3 text-[11.5px]">
          <div className="font-medium mb-1">✓ Preview looks good · {preview.rows_parsed} row{preview.rows_parsed === 1 ? "" : "s"} ready to import</div>
          <div className="text-foreground/55">Click <strong>Import orders</strong> above to commit them to the fulfillment queue.</div>
        </div>
      )}

      {preview && preview.mode === "schema_error" && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-[11.5px]">
          <div className="font-medium text-amber-800 mb-1 flex items-center gap-1">
            <AlertTriangle className="w-3.5 h-3.5"/>Column mismatch
          </div>
          <div className="text-amber-800">
            Missing: <span className="mono">{preview.missing.join(", ")}</span>
          </div>
          <div className="text-amber-800/70 mt-1">
            Download the template above — your CSV must include every column with the exact same name.
          </div>
        </div>
      )}

      {preview && preview.mode === "result" && (
        <div className="rounded-lg border border-emerald-300 bg-emerald-50 p-3 text-[11.5px]" data-testid="wholesale-csv-result">
          <div className="font-medium text-emerald-800 mb-1">Import complete</div>
          <div className="text-emerald-800 grid grid-cols-3 gap-2 mt-1">
            <div><span className="font-semibold">{preview.count_created}</span> created</div>
            <div><span className="font-semibold">{preview.count_duplicates}</span> duplicates</div>
            <div><span className="font-semibold">{preview.count_errors}</span> errors</div>
          </div>
          {preview.errors?.length > 0 && (
            <details className="mt-2 text-amber-800">
              <summary className="cursor-pointer">Show error rows</summary>
              <ul className="mono text-[10.5px] mt-1 max-h-32 overflow-auto">
                {preview.errors.map((e, i) => (
                  <li key={i}>row {e.row}: {e.error}</li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}
    </div>
  );
}

// ───────────────────── Tab 2 · Email Forwarding ─────────────────────

function EmailForwardingTab({ channel }) {
  const [inbox, setInbox] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [allowed, setAllowed] = useState("");

  useEffect(() => {
    api.get(`/dropship/channels/${channel.id}/email-inbox`)
      .then(r => {
        setInbox(r.data);
        setAllowed((r.data?.allowed_senders || []).join(", "));
      })
      .catch(() => setInbox(null))
      .finally(() => setLoading(false));
  }, [channel.id]);

  const provision = async () => {
    setBusy(true);
    try {
      const list = allowed.split(",").map(s => s.trim()).filter(Boolean);
      const r = await api.post(`/dropship/channels/${channel.id}/email-inbox`,
                               { allowed_senders: list });
      setInbox(r.data);
      toast.success("Inbox provisioned");
    } catch { toast.error("Could not provision"); }
    finally { setBusy(false); }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(inbox?.address || "");
      toast.success("Address copied");
    } catch { toast.error("Copy failed — select manually"); }
  };

  if (loading) return <div className="py-6 grid place-items-center"><Loader2 className="w-4 h-4 animate-spin text-foreground/40"/></div>;

  return (
    <div className="space-y-4" data-testid="wholesale-email-tab">
      {!inbox ? (
        <div className="rounded-lg border border-dashed border-border bg-secondary/20 p-4 text-[11.5px]">
          <div className="text-[12.5px] font-medium mb-1">Forward-by-email is off</div>
          <div className="text-foreground/60 mb-3">
            Provision a unique inbox address. Add one forwarding rule in your
            email client — retailer POs land here automatically.
          </div>
          <div className="mb-3">
            <Label className="text-[11px] text-foreground/60">Allowed sender addresses (optional · comma-separated)</Label>
            <Input
              placeholder="orders@macys.com, po@nordstrom.com"
              value={allowed}
              onChange={e => setAllowed(e.target.value)}
              data-testid="wholesale-email-allowed-senders"
            />
          </div>
          <Button onClick={provision} disabled={busy}
                  data-testid="wholesale-email-provision"
                  className="rounded-full h-8 px-3 text-[12px]">
            {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin"/> : <Mail className="w-3.5 h-3.5 mr-1"/>}
            Provision inbox
          </Button>
        </div>
      ) : (
        <>
          <div>
            <Label className="text-[10.5px] mono uppercase tracking-[0.14em] text-foreground/50 block mb-1">
              Your channel inbox address
            </Label>
            <div className="flex items-center gap-2">
              <code data-testid="wholesale-email-address"
                    className="flex-1 mono text-[12px] rounded-md bg-secondary/60 px-3 py-2 truncate border border-border">
                {inbox.address}
              </code>
              <Button variant="outline" size="sm" onClick={copy}
                      data-testid="wholesale-email-copy">
                <Copy className="w-3.5 h-3.5 mr-1"/>Copy
              </Button>
            </div>
          </div>

          <div className="rounded-lg border border-border bg-secondary/20 p-3 text-[11.5px]">
            <div className="text-[12px] font-medium mb-1">Gmail setup (one-time)</div>
            <ol className="list-decimal ml-4 space-y-1 text-foreground/70">
              <li>Gmail → Settings → Filters and Blocked Addresses → Create a new filter.</li>
              <li>&quot;From&quot; = your retailer&apos;s PO address (e.g., <span className="mono">orders@macys.com</span>).</li>
              <li>Click <strong>Create filter</strong>.</li>
              <li>Check <strong>Forward it to</strong> → add the address above.</li>
              <li>Save. Every future PO from that sender lands here automatically.</li>
            </ol>
          </div>

          <div className="grid grid-cols-3 gap-2 text-center">
            <Stat label="Orders received" value={inbox.orders_received || 0}/>
            <Stat label="Last received" value={inbox.last_received_at ? new Date(inbox.last_received_at).toLocaleDateString() : "—"}/>
            <Stat label="Allowed senders" value={(inbox.allowed_senders || []).length || "any"}/>
          </div>

          <div>
            <Label className="text-[11px] text-foreground/60">Update allowed senders</Label>
            <div className="flex items-center gap-2">
              <Input
                value={allowed}
                onChange={e => setAllowed(e.target.value)}
                placeholder="Leave blank to accept from any sender"
                data-testid="wholesale-email-allowed-senders-edit"
              />
              <Button variant="outline" size="sm" onClick={provision} disabled={busy}>
                {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : "Save"}
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-md border border-border bg-white px-2 py-1.5">
      <div className="text-[9.5px] mono uppercase tracking-[0.14em] text-foreground/45">{label}</div>
      <div className="text-[13px] font-medium mt-0.5">{value}</div>
    </div>
  );
}

// ───────────────────── Tab 3 · Scheduled Import ─────────────────────

function ScheduledImportTab({ channel, onDone }) {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    source_url: "",
    frequency:  "hourly",
    enabled:    true,
    auth_header_name:  "",
    auth_header_value: "",
  });

  const load = () => {
    setLoading(true);
    api.get(`/dropship/channels/${channel.id}/scheduled-import`)
      .then(r => {
        setConfig(r.data);
        setForm({
          source_url:        r.data.source_url || "",
          frequency:         r.data.frequency  || "hourly",
          enabled:           r.data.enabled !== false,
          auth_header_name:  r.data.auth_header_name  || "",
          auth_header_value: r.data.auth_header_value || "",
        });
      })
      .catch(() => setConfig(null))
      .finally(() => setLoading(false));
  };
  useEffect(load, [channel.id]); // eslint-disable-line

  const save = async () => {
    if (!form.source_url.trim()) { toast.error("Source URL is required"); return; }
    setBusy(true);
    try {
      await api.put(`/dropship/channels/${channel.id}/scheduled-import`, form);
      toast.success("Schedule saved");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  const runNow = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/dropship/channels/${channel.id}/scheduled-import/run`);
      toast.success(`Fetched · ${r.data.created} new · ${r.data.duplicates} dupes`);
      load();
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fetch failed");
      load();
    } finally { setBusy(false); }
  };

  if (loading) return <div className="py-6 grid place-items-center"><Loader2 className="w-4 h-4 animate-spin text-foreground/40"/></div>;

  return (
    <div className="space-y-4" data-testid="wholesale-scheduled-tab">
      <div className="rounded-lg border border-border bg-secondary/20 p-3 text-[11.5px] flex items-start gap-2">
        <Info className="w-3.5 h-3.5 shrink-0 mt-0.5 text-foreground/50"/>
        <div className="text-foreground/70">
          Paste any URL that returns a CSV — a published Google Sheet, a signed
          S3 link, a Dropbox share, or any HTTPS endpoint. We poll on the
          cadence below and ingest only new rows (idempotent on external_order_id).
        </div>
      </div>

      <div>
        <Label className="text-[11px] text-foreground/60">Source URL</Label>
        <Input
          placeholder="https://docs.google.com/spreadsheets/d/…/edit"
          value={form.source_url}
          onChange={e => setForm({ ...form, source_url: e.target.value })}
          data-testid="wholesale-scheduled-url"
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-[11px] text-foreground/60">Frequency</Label>
          <select
            data-testid="wholesale-scheduled-frequency"
            className="w-full mt-1 h-9 rounded-md border border-border bg-background px-2 text-[13px]"
            value={form.frequency}
            onChange={e => setForm({ ...form, frequency: e.target.value })}
          >
            <option value="every_15m">Every 15 min</option>
            <option value="hourly">Hourly</option>
            <option value="every_6h">Every 6 hours</option>
            <option value="daily">Daily</option>
            <option value="manual">Manual only (Run Now)</option>
          </select>
        </div>
        <div className="flex items-end">
          <label className="text-[11.5px] inline-flex items-center gap-2 h-9">
            <input
              type="checkbox"
              checked={form.enabled}
              onChange={e => setForm({ ...form, enabled: e.target.checked })}
              data-testid="wholesale-scheduled-enabled"
            />
            Enabled
          </label>
        </div>
      </div>

      <details className="rounded-md border border-border bg-white">
        <summary className="cursor-pointer px-3 py-1.5 text-[11.5px] text-foreground/60">
          Advanced · Custom auth header (S3 signed URL, private API)
        </summary>
        <div className="p-3 grid grid-cols-2 gap-2">
          <Input
            placeholder="Header name (e.g., Authorization)"
            value={form.auth_header_name}
            onChange={e => setForm({ ...form, auth_header_name: e.target.value })}
          />
          <Input
            placeholder="Header value"
            value={form.auth_header_value}
            onChange={e => setForm({ ...form, auth_header_value: e.target.value })}
          />
        </div>
      </details>

      <div className="flex items-center gap-2 flex-wrap">
        <Button onClick={save} disabled={busy || !form.source_url}
                data-testid="wholesale-scheduled-save"
                className="rounded-full h-8 px-3 text-[12px]">
          {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin"/> : <Check className="w-3.5 h-3.5 mr-1"/>}
          Save schedule
        </Button>
        {config && (
          <Button variant="outline" onClick={runNow} disabled={busy}
                  data-testid="wholesale-scheduled-run-now"
                  className="rounded-full h-8 px-3 text-[12px]">
            {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin"/> : <Play className="w-3.5 h-3.5 mr-1"/>}
            Run now
          </Button>
        )}
      </div>

      {config && (
        <div className="grid grid-cols-3 gap-2 mt-2">
          <Stat label="Total runs" value={config.runs || 0}/>
          <Stat label="Last run" value={config.last_run_at ? new Date(config.last_run_at).toLocaleString() : "—"}/>
          <Stat label="Status" value={
            config.last_result
              ? (config.last_result.ok ? "OK" : "Failed")
              : "Never run"
          }/>
        </div>
      )}

      {config?.last_result && !config.last_result.ok && (
        <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-[11.5px] text-rose-800">
          <div className="font-medium mb-1 flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5"/>Last run failed</div>
          <div className="mono text-[10.5px]">{config.last_result.error}</div>
        </div>
      )}
    </div>
  );
}
